import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChecklistTemplateColumn } from '@portal-core/checklist-templates/models/checklist-template-column.model';
import { ChecklistTemplate } from '@portal-core/checklist-templates/models/checklist-template.model';
import { ChecklistTemplatesService } from '@portal-core/checklist-templates/services/checklist-templates.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklistColumn } from '@portal-core/project-checklists/models/project-checklist-column.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { ProjectChecklistsService } from '@portal-core/project-checklists/services/project-checklists/project-checklists.service';
import { LoadingState } from '@portal-core/util/loading-state';
import { first, Observable } from 'rxjs';

export interface ProjectChecklistColumnsForm {
  addColumn: FormControl<string>;
  columns: FormArray;
}

export interface FormData {
  message: string,
  object: ChecklistTemplate | ProjectChecklist,
}

@Component({
  selector: 'mc-project-checklist-columns-form',
  templateUrl: './project-checklist-columns-form.component.html',
  styleUrls: ['./project-checklist-columns-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectChecklistColumnsFormComponent implements OnChanges {
  @Input() checklist?: ProjectChecklist;
  @Input() licenseId: number;
  @Input() projectId: number;
  @Input() showFormFooter?: boolean = true;
  @Input() template?: ChecklistTemplate;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('columnList', { static: true }) checklistColumnList: ElementRef<HTMLElement>;

  ProjectChecklistType: typeof ProjectChecklistType = ProjectChecklistType;
  checklistColumnsForm: FormGroup<ProjectChecklistColumnsForm>;
  savingState: LoadingState<string> = new LoadingState<string>();

  get addColumn(): FormControl<string> {
    return this.checklistColumnsForm?.get('addColumn') as FormControl<string>;
  }

  get columns(): FormArray {
    return this.checklistColumnsForm?.get('columns') as FormArray;
  }

  get dirty(): boolean {
    return this.checklistColumnsForm?.controls.columns.dirty;
  }

  constructor(
    private fb: FormBuilder,
    private errorService: ErrorService,
    private projectChecklistsService: ProjectChecklistsService,
    private checklistTemplatesService: ChecklistTemplatesService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.checklist || changes.template) {
      this.resetForm();
    }
  }

  onAddColumn() {
    const addColumnInput = this.checklistColumnsForm.get('addColumn');

    if (addColumnInput?.value) {
      const newColumn = this.fb.group({
        ChecklistId: this.checklist && this.checklist.Id ? this.checklist.Id : 0,
        Name: addColumnInput.value,
        Order: this.columns.length
      });

      this.columns.push(newColumn);
      this.columns.markAsDirty();
      addColumnInput.setValue('');

      // Scroll to the bottom of the list where the new column was added
      this.ngZone.onStable.asObservable().pipe(first()).subscribe(() => {
        this.checklistColumnList.nativeElement.scrollTop = this.checklistColumnList.nativeElement.scrollHeight;
      });
    }
  }

  onCancelClicked() {
    this.cancel.emit();
  }

  onMoveColumn(column: ProjectChecklistColumn, moveUp: boolean) {
    const prevIndex = this.columns.controls.findIndex(c => c.value === column);
    const moveUpIndex = prevIndex - 1 <= 0 ? 0 : prevIndex - 1;
    const moveDownIndex = (prevIndex + 1 >= this.columns.length ? prevIndex : prevIndex + 1);
    const newIndex = moveUp ? moveUpIndex : moveDownIndex;

    // If moving to the end and the column is already at the end then do nothing
    if ((moveUp && prevIndex == 0) || (!moveUp && prevIndex == this.columns.length - 1)) {
      return;
    }

    const newColumn = this.fb.group({
      Id: column.Id,
      ChecklistId: column.ChecklistId,
      Name: column.Name,
      Order: column.Order,
    });

    this.onRemoveColumn(column);
    this.columns.insert(newIndex, newColumn);
    // Update the list order
    this.columns.controls.forEach((control, index) => {
      control.value['Order'] = index;
    });
    this.columns.patchValue(this.columns.value);
    this.columns.markAsDirty();
  }

  onRemoveColumn(column: ProjectChecklistColumn) {
    const index = this.columns.controls.findIndex(c => c.value === column);
    this.columns.removeAt(index);
    this.columns.markAsDirty();
  }

  onSubmit() {
    if (this.checklist) {
      this.checklistFormSubmit((checklist) => this.projectChecklistsService.updateProjectChecklist$(checklist));
    } else if (this.template) {
      this.templateFormSubmit((template) => this.checklistTemplatesService.updateChecklistTemplate$(template));
    }
  }

  public getChecklistTemplateColumns(): ChecklistTemplateColumn[] {
    const columns: ChecklistTemplateColumn[] = [];

    this.columns.value.forEach(column => {
      const templateColumn: ChecklistTemplateColumn = {
        ChecklistTemplateId: column.ChecklistTemplateId,
        Name: column.Name,
        Order: column.Order
      };

      columns.push(templateColumn);
    });

    return columns;
  }

  private getProjectChecklistColumns(): ProjectChecklistColumn[] {
    const columns: ProjectChecklistColumn[] = [];

    this.columns.value.forEach(column => {
      if (!column.Id)
        column.Id = 0; // account for newly added columns

      columns.push(column);
    });

    return columns;
  }

  // submit via profile dialog
  private checklistFormSubmit(func: (checklist: ProjectChecklist) => Observable<any>) {
    const checklist: ProjectChecklist = {
      ...this.checklist,
      Columns: this.getProjectChecklistColumns()
    };

    const dataObj: FormData = {
      object: checklist,
      message: 'checklist'
    };

    this.formSubmit(func, dataObj);
  }

  // submit via profile dialog
  private templateFormSubmit(func: (template: ChecklistTemplate) => Observable<any>) {
    const template: ChecklistTemplate = {
      ...this.template,
      Columns: this.getChecklistTemplateColumns()
    };

    const dataObj: FormData = {
      object: template,
      message: 'checklist template'
    };

    this.formSubmit(func, dataObj);
  }

  private formSubmit(func: (obj: ChecklistTemplate | ProjectChecklist) => Observable<any>, formData: FormData) {
    this.savingState.update(true);
    func(formData.object).pipe(first()).subscribe(() => {
      this.savingState.update(false);
      this.checklistColumnsForm.markAsPristine();
      this.snackBar.open('The ' + formData.message + ' has been updated.', 'OK', { duration: 2500 });
      this.saved.emit();
    }, error => {
      this.savingState.update(false, 'Unable to update the ' + formData.message, this.errorService.getErrorMessages(error));
    });
  }

  private buildForm() {
    this.checklistColumnsForm = this.fb.group<ProjectChecklistColumnsForm>({
      addColumn: new FormControl(null),
      columns: new FormArray([])
    });
  }

  private resetForm() {
    this.columns.clear();

    if (this.checklist?.Columns) {
      this.checklist.Columns.forEach(item => {
        this.columns.push(this.fb.group({
          Id: item.Id,
          ChecklistId: item.ChecklistId,
          Name: item.Name,
          Order: item.Order,
        }));
      });
    } else if (this.template?.Columns) {
      this.template?.Columns?.forEach(item => {
        this.columns.push(this.fb.group({
          ChecklistTemplateId: 0,
          Name: item.Name,
          Order: item.Order,
        }));
      });
    }
  }
}
